// AnimatedBadge.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { LRUCache } from 'lru-cache';

const SPRITE_FRAMES_PER_SECOND = 24;

// LRU because src can be a decrypted blob url, which churns over a long
// session and would grow a plain Map without bound.
const resolvedFrameCounts = new LRUCache({ max: 256 });
const pendingProbes = new Map();

const probeFrameCount = src => {
  const resolved = resolvedFrameCounts.get(src);
  if (resolved) {
    return Promise.resolve(resolved);
  }
  const pending = pendingProbes.get(src);
  if (pending) {
    return pending;
  }
  const probe = new Promise(resolve => {
    const image = new Image();
    image.fetchPriority = 'low';
    image.onload = () => {
      const { naturalWidth, naturalHeight } = image;
      const isValidStrip =
        naturalHeight > 0 && naturalWidth % naturalHeight === 0;
      const probedFrameCount = isValidStrip ? naturalWidth / naturalHeight : 0;
      // A single square frame is not a strip; treat it as no animation.
      const frameCount = probedFrameCount < 2 ? null : probedFrameCount;
      if (frameCount) {
        resolvedFrameCounts.set(src, frameCount);
      }
      pendingProbes.delete(src);
      resolve(frameCount);
    };
    image.onerror = () => {
      pendingProbes.delete(src);
      resolve(null);
    };
    image.src = src;
  });
  pendingProbes.set(src, probe);
  return probe;
};

// Sprite sheet contract: horizontal strip of square frames, no gap, so the
// frame count can be derived from the image dimensions alone.
export const AnimatedBadge = ({ src = '', size = 20, alt = '' }) => {
  const [probeResult, setProbeResult] = useState(null);
  useEffect(() => {
    // src is empty until DecryptionWrapper (when enabled) injects the blob url.
    if (!src) {
      return;
    }
    let isActive = true;
    // Failures (null) are stored too, so a src that no longer resolves to a
    // valid strip falls back to the placeholder instead of keeping the
    // previous frame count forever.
    probeFrameCount(src).then(frameCount => {
      if (isActive) {
        setProbeResult({ src, frameCount });
      }
    });
    return () => {
      isActive = false;
    };
  }, [src]);
  // The probe result records the src it belongs to; a result from a previous
  // src is ignored so its frame count is never paired with the current image.
  const frameCount =
    probeResult?.src === src
      ? probeResult.frameCount
      : resolvedFrameCounts.get(src) || null;
  if (!frameCount) {
    return <StyledPlaceholder $size={size} />;
  }
  return (
    <StyledViewport role="img" aria-label={alt} $size={size}>
      {/* The strip is decorative; the viewport carries the accessible name. */}
      <StyledSprite
        src={src}
        alt=""
        aria-hidden="true"
        $size={size}
        $frameCount={frameCount}
      />
    </StyledViewport>
  );
};

AnimatedBadge.propTypes = {
  src: PropTypes.string,
  size: PropTypes.number,
  alt: PropTypes.string,
};

const StyledPlaceholder = styled.div`
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const spritePlay = ({ $size, $frameCount }) => keyframes`
  to {
    transform: translateX(${-$size * $frameCount}px);
  }
`;

const StyledViewport = styled.div`
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  overflow: hidden;
`;

// Animating transform instead of background-position keeps each step on the
// compositor, so a chatroom full of badges does not repaint on every frame.
const StyledSprite = styled.img`
  display: block;
  width: ${({ $size, $frameCount }) => $size * $frameCount}px;
  height: ${({ $size }) => $size}px;
  animation: ${spritePlay}
    ${({ $frameCount }) => $frameCount / SPRITE_FRAMES_PER_SECOND}s
    steps(${({ $frameCount }) => $frameCount}) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default AnimatedBadge;
